import type { Course, CurriculumType, ProgrammeModuleType, ProgrammeType } from '@/types/composition-functions';
import { ProgressionTypeEnum } from '@/types/composition-functions';
import { semesterLabel } from '@/utils/semesterLabel';
import { formatCourseInstance } from '@/utils/courseInstance';
import type { CurriculumRow } from './curriculumGrouping';

// All shapes here are derived from the generated schema types so they cannot
// drift from the backend (see scripts/check-handwritten-types.mjs). The
// Partial<> parts are fields the curriculum queries do not always select.
export type CurriculumCourse = Pick<Course, 'id' | 'course_title' | 'course_code' | 'course_instance_id' | 'unit'>
  & { course_instance_year?: number | null };

export type CurriculumProgramme = Pick<ProgrammeType, 'id' | 'program_name' | 'degree' | 'type'>
  & Partial<Pick<ProgrammeType, 'duration' | 'delivery_mode' | 'progression_type'>>;

export type CurriculumItem = Pick<CurriculumType, 'id' | 'programme_id' | 'course_registration_level' | 'semester' | 'status'> & {
  programme?: CurriculumProgramme | null;
  course?: CurriculumCourse | CurriculumCourse[] | null;
  programme_module_id?: string | null;
  moduleOfferings?: Pick<ProgrammeModuleType, 'id' | 'name'>[];
  programmeModule?: Pick<ProgrammeModuleType, 'id' | 'name'> | null;
};

/**
 * Table row for the curriculum views. `semester` is only the rendered label
 * ("First Semester", "Cumulative Sem 3", "Any Semester", …); `semester_value`
 * carries the raw numeric semester so editing never has to reverse-parse
 * display text back into a number.
 */
export interface CurriculumTableRow extends CurriculumRow, Record<string, unknown> {
  semester_value: number;
  module_name: string;
  programme_module_id: string | null;
}

export type CurriculumEditModel =
  Pick<CurriculumType, 'id' | 'programme_id' | 'course_registration_level' | 'semester' | 'status' | 'course_id'>;

/** Display label for a curriculum entry's semester, honouring year-based programmes. */
export function curriculumSemesterLabel(item: Pick<CurriculumItem, 'semester' | 'programme'>): string {
  if (item.semester === 0) return 'Any Semester';
  if (item.programme?.progression_type === ProgressionTypeEnum.Year) {
    return `Cumulative Sem ${item.semester}`;
  }
  return semesterLabel(item.semester);
}

export function buildCurriculumRow(item: CurriculumItem): CurriculumTableRow {
  const courses: CurriculumCourse[] = Array.isArray(item.course)
    ? item.course
    : item.course ? [item.course] : [];
  const programme = item.programme;
  const programmeLabel = programme
    ? [programme.program_name, programme.degree, programme.type ? `(${programme.type})` : null]
      .filter(Boolean).join(' ')
    : '';

  return {
    id: item.id,
    programme_name: programmeLabel,
    programme_id: item.programme_id,
    course_registration_level: item.course_registration_level === '*' ? 'Any Level' : item.course_registration_level,
    course_id: Array.isArray(item.course) ? '' : item.course?.id || '',
    semester: curriculumSemesterLabel(item),
    semester_value: item.semester,
    status: item.status === 'C' ? 'Compulsory'
      : item.status === 'E' ? 'Elective'
        : item.status,
    module_name: [...new Set([...(item.moduleOfferings ?? []).map(m => m.name), item.programmeModule?.name].filter(Boolean))].join(', ') || '—',
    programme_module_id: item.programme_module_id ?? null,
    course_titles: courses.map(c => c.course_title).join(', '),
    course_units: courses.map(c => c.unit).join(', '),
    course_codes: courses.map(c => c.course_code).join(', '),
    course_instances: courses.map(c => formatCourseInstance(c)).join(', '),
  };
}

/**
 * Edit-dialog model for a table row. The semester comes from the row's raw
 * `semester_value` — reverse-parsing the rendered label only knew a handful of
 * label shapes and silently collapsed the rest ("Cumulative Sem 3",
 * "Fifth Semester", …) to 0, i.e. "any semester".
 */
export function rowToEditModel(row: CurriculumTableRow): CurriculumEditModel {
  return {
    id: row.id,
    programme_id: row.programme_id,
    course_registration_level: row.course_registration_level === 'Any Level' ? '*' : row.course_registration_level,
    semester: row.semester_value,
    status: row.status === 'Compulsory' ? 'C'
      : row.status === 'Elective' ? 'E'
        : '',
    course_id: row.course_id,
  };
}
